import { BrowserRouter, Routes, Route, Link, NavLink, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { Screen, Spinner } from "./components/ui";
import { SignIn } from "./components/SignIn";
import { Gallery } from "./routes/Gallery";
import { Buy } from "./routes/Buy";
import { Create } from "./routes/Create";
import { Capture } from "./routes/Capture";
import { Prints } from "./routes/Prints";
import { Orders } from "./routes/Orders";

function Nav() {
  const { signedIn, credits, email, signOut } = useAuth();
  const link = ({ isActive }: { isActive: boolean }) =>
    `px-3 py-2 text-[13px] rounded-lg transition ${
      isActive ? "text-gold bg-gold/10" : "text-warmStone hover:text-parchment"
    }`;
  return (
    <nav className="mb-7 flex flex-wrap items-center gap-1 border-b border-gold/15 pb-3">
      <Link to="/" className="mr-3 font-serif text-xl text-goldBright">
        HolyShot
      </Link>
      {signedIn && (
        <>
          <NavLink to="/create" className={link}>
            Create
          </NavLink>
          <NavLink to="/gallery" className={link}>
            Gallery
          </NavLink>
          <NavLink to="/prints" className={link}>
            Prints
          </NavLink>
          <NavLink to="/orders" className={link}>
            Orders
          </NavLink>
        </>
      )}
      <div className="ml-auto flex items-center gap-2">
        {signedIn ? (
          <>
            <Link
              to="/buy"
              className="rounded-full border border-gold/30 px-3 py-1.5 text-[12px] text-gold hover:border-gold/60"
              title={email || undefined}
            >
              {credits?.unlimited ? "Unlimited" : credits ? `${credits.available} credits` : "Credits"}
            </Link>
            <button onClick={() => void signOut()} className="text-[12px] text-stoneDim hover:text-warmStone">
              Sign out
            </button>
          </>
        ) : null}
      </div>
    </nav>
  );
}

/** Routes that need an account. Everything the web app does hangs off one. */
function Protected({ children }: { children: React.ReactNode }) {
  const { signedIn, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!signedIn) return <SignIn reason="Sign in to see your portraits, credits and orders." />;
  return <>{children}</>;
}

function Home() {
  const { signedIn, loading } = useAuth();
  if (loading) return <Spinner />;
  return signedIn ? <Navigate to="/create" replace /> : <SignIn />;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Screen>
          <Nav />
          <Routes>
            <Route path="/" element={<Home />} />
            {/* The QR landing. Deliberately NOT wrapped in Protected: the token
                itself carries identity, so a phone that has never signed in can
                still land here and capture. */}
            <Route path="/capture" element={<Capture />} />
            <Route path="/buy" element={<Protected><Buy /></Protected>} />
            <Route path="/gallery" element={<Protected><Gallery /></Protected>} />
            <Route path="/create" element={<Protected><Create /></Protected>} />
            <Route path="/prints" element={<Protected><Prints /></Protected>} />
            <Route path="/orders" element={<Protected><Orders /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Screen>
      </AuthProvider>
    </BrowserRouter>
  );
}
